import { GOOGLE_FONTS, useGoogleFonts } from '~/client/common/hooks/use-google-fonts.hook'
import { EventSpaceList } from '~/shared/event/event.types'
import React from 'react'
import defaultCoverImage from '~/assets/images/presentations/freshfields/image.png'
import { formatUtcDateLongFromUnknown } from '~/shared/common/date'

const Container: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="bg-[#f9f8ee] w-[1920px] h-[1080px] min-w-[1920px] min-h-[1080px] max-w-[1920px] max-h-[1080px] p-0 m-0 font-['Nunito_Sans',sans-serif] relative overflow-hidden flex flex-col">
    {children}
  </div>
)

interface FreshfieldsSlideCoverProps {
  eventSpaceList: EventSpaceList
  coverImage?: string | null
}

const FreshfieldsSlideCover: React.FC<FreshfieldsSlideCoverProps> = ({ eventSpaceList, coverImage }) => {
  useGoogleFonts([GOOGLE_FONTS.NUNITO_SANS, GOOGLE_FONTS.MAITREE])

  if (!eventSpaceList) {
    return null
  }

  // Get the first event date if available
  const eventDate = formatUtcDateLongFromUnknown(eventSpaceList.event_space_list_dates?.[0]?.start_date, 'en-US') ?? ''

  return (
    <Container>
      {/* Freshfields Logo */}
      <div className="absolute top-[138px] left-[76px] font-['Arial',sans-serif] text-[52px] font-bold text-black tracking-[2px]">
        <svg width="575" height="52" viewBox="0 0 575 52" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_403_3807)">
            <path
              d="M0 0.942517V50.8402H14.1532V36.5364H47.0236V23.6183H14.1532V13.8605H49.8954V0.942517H0ZM91.329 50.8402H108.405L97.2106 35.0717L81.3353 36.3146L91.329 50.8402ZM53.7412 50.8402H67.8945V13.8605H88.406C91.329 13.8605 93.0213 15.6901 93.0213 18.8503C93.0213 22.0105 91.329 23.8956 88.406 23.8956H67.8945V36.3146H85.8937L86.919 36.3701H89.175C100.867 36.3701 107.892 29.6061 107.892 18.2405C107.892 7.15203 101.329 0.942517 89.6372 0.942517H53.7412V50.8402ZM111.225 0.942517V50.8402H162.044V39.0311H125.379V31.3248H158.403V20.181H125.379V12.7517H161.736V0.942517H111.225ZM163.018 34.4296C163.582 46.7377 171.94 51.7831 191.581 51.7831C210.708 51.7831 219.016 46.7933 219.016 35.2058C219.016 25.836 213.99 21.1789 203.17 20.4582L184.709 19.183C180.504 18.9058 179.068 18.0187 179.068 15.6901C179.068 12.3082 182.145 11.0884 190.555 11.0884C199.273 11.0884 202.555 12.6409 203.016 16.9653H218.298C217.58 4.98982 209.426 0 190.607 0C172.505 0 164.146 5.267 164.146 16.7435C164.146 25.947 169.12 30.2714 180.504 31.0476L198.299 32.2673C202.555 32.5446 204.093 33.4871 204.093 35.9266C204.093 39.4749 200.965 40.6391 191.786 40.6391C182.505 40.6391 178.966 39.0312 178.351 34.4296H163.018ZM236.348 0.942517H222.195V50.8402H236.348V31.7684H264.039V50.8402H278.192V0.942517H264.039V18.8503H236.348V0.942517ZM283.628 0.942517V50.8402H297.781V36.5364H330.651V23.6183H297.781V13.8605H333.523V0.942517H283.628ZM351.469 0.942517H337.369V50.8402H351.469V0.942517ZM356.905 0.942517V50.8402H407.723V39.0311H371.061V31.3248H404.084V20.181H371.061V12.7517H407.416V0.942517H356.905ZM411.569 50.8402H456.132V37.9226H425.672V0.942517H411.569V50.8402ZM490.746 37.9226H474.029V13.8605H490.746C499.721 13.8605 503.516 17.5197 503.516 26.0578C503.516 34.4296 499.721 37.9226 490.746 37.9226ZM492.952 0.942517H459.877V50.8402H491.414C508.281 50.8402 518.128 41.4706 518.128 25.448C518.128 10.0351 508.796 0.942517 492.952 0.942517ZM518.999 34.4296C519.563 46.7377 527.921 51.7831 547.561 51.7831C566.692 51.7831 575 46.7933 575 35.2058C575 25.836 569.972 21.1789 559.15 20.4582L540.693 19.183C536.487 18.9058 535.052 18.0187 535.052 15.6901C535.052 12.3082 538.13 11.0884 546.537 11.0884C555.256 11.0884 558.537 12.6409 558.997 16.9653H574.278C573.565 4.98982 565.41 0 546.591 0C528.49 0 520.133 5.267 520.133 16.7435C520.133 25.947 525.106 30.2714 536.487 31.0476L554.281 32.2673C558.537 32.5446 560.076 33.4871 560.076 35.9266C560.076 39.4749 556.948 40.6391 547.769 40.6391C538.486 40.6391 534.948 39.0312 534.334 34.4296H518.999Z"
              fill="black"
            />
          </g>
          <defs>
            <clipPath id="clip0_403_3807">
              <rect width="575" height="52" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>

      {/* Blue Box */}
      <div className="absolute top-[306px] left-[34px] w-[1154px] h-[720px] bg-[#728efc] rounded-tl-[55px]" />

      {/* Cover Image on Right */}
      <div className="absolute top-[306px] left-[1218px] w-[666px] h-[720px] overflow-hidden">
        <img src={coverImage || defaultCoverImage} alt="Cover" className="w-full h-full object-cover object-center" />
      </div>

      {/* Main Title - Centered on Blue Box */}
      <div className="absolute top-[605px] left-[76px] w-[1068px] -translate-y-1/2 flex flex-col justify-center">
        <div className="font-['Maitree',serif] text-[100px] font-bold text-black leading-[100px]">{eventSpaceList.name || 'Event Name'}</div>
      </div>

      {/* VENUE OPTIONS Label */}
      <div className="absolute top-[843px] left-[76px]">
        <div className="font-['Nunito_Sans',sans-serif] text-[45px] font-semibold text-black tracking-[0]">VENUE OPTIONS</div>
      </div>

      {/* Date */}
      {eventDate && (
        <div className="absolute top-[936px] left-[76px]">
          <div className="font-['Nunito_Sans',sans-serif] text-[30px] font-semibold text-black">{eventDate}</div>
        </div>
      )}
    </Container>
  )
}

export default FreshfieldsSlideCover
